import type { JSX, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import styles from './DefilementHorizontal.module.css';

/** Tolérance d'arrondi (px) : les positions de défilement sont fractionnaires. */
const TOLERANCE = 1;

interface BordsCaches {
  debut: boolean;
  fin: boolean;
}

function useBordsCaches(): [React.RefObject<HTMLDivElement | null>, BordsCaches] {
  const zone = useRef<HTMLDivElement>(null);
  const [bords, setBords] = useState<BordsCaches>({ debut: false, fin: false });
  useEffect(() => {
    const element = zone.current;
    if (!element) return;
    const mesurer = (): void => {
      const debut = element.scrollLeft > TOLERANCE;
      const fin = element.scrollLeft + element.clientWidth < element.scrollWidth - TOLERANCE;
      setBords((courant) =>
        courant.debut === debut && courant.fin === fin ? courant : { debut, fin },
      );
    };
    mesurer();
    element.addEventListener('scroll', mesurer, { passive: true });
    if (typeof ResizeObserver === 'undefined') {
      return () => element.removeEventListener('scroll', mesurer);
    }
    // La zone change de largeur (redimensionnement) et son contenu aussi
    // (lignes ajoutées, colonnes) : on mesure dans les deux cas.
    const observateur = new ResizeObserver(mesurer);
    observateur.observe(element);
    for (const enfant of Array.from(element.children)) observateur.observe(enfant);
    return () => {
      element.removeEventListener('scroll', mesurer);
      observateur.disconnect();
    };
  }, []);
  return [zone, bords];
}

export interface DefilementHorizontalProps {
  children: ReactNode;
  /** Classe de mise en page de l'écran appelant (cadre, marges). */
  className?: string;
}

/**
 * Zone qui défile horizontalement quand son contenu (un tableau) dépasse, et
 * l'indique : le contenu s'estompe vers le bord où il continue. Même indice
 * que les onglets défilants de `@artosera/ui` (`Tabs`), sans quoi une colonne
 * coupée n'a aucune raison d'être cherchée (audit du 21 octobre 2026).
 */
export function DefilementHorizontal({
  children,
  className,
}: DefilementHorizontalProps): JSX.Element {
  const [zone, bords] = useBordsCaches();
  return (
    <div
      ref={zone}
      className={[styles.zone, className].filter(Boolean).join(' ')}
      data-debut-cache={bords.debut ? 'oui' : undefined}
      data-fin-cachee={bords.fin ? 'oui' : undefined}
    >
      {children}
    </div>
  );
}
